import React, { useCallback, useRef, useState } from 'react';
import { MoveHorizontal } from 'lucide-react';
type CompareSliderProps = {
  before: string;
  after: string;
  alt: string;
};
export function CompareSlider({
  before,
  after,
  alt
}: CompareSliderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(50);
  const [dragging, setDragging] = useState(false);
  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = (clientX - rect.left) / rect.width * 100;
    setPosition(Math.min(100, Math.max(0, pct)));
  }, []);
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
    updateFromClientX(event.clientX);
  };
  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    updateFromClientX(event.clientX);
  };
  const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };
  return <div ref={containerRef} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={stopDragging} onPointerCancel={stopDragging} className="relative aspect-[3/2] w-full touch-none select-none overflow-hidden rounded-2xl bg-navy-900 shadow-[0_40px_80px_-45px_rgba(0,0,0,0.9)]" data-id="element-81" data-mp="1ss7amyndbb5z">
      <img src={after} alt={`${alt} — after`} draggable={false} className="absolute inset-0 h-full w-full object-cover" data-id="element-82" data-mp="2bg2ik1watur6" />
      <div className="absolute inset-0 overflow-hidden" style={{
      clipPath: `inset(0 ${100 - position}% 0 0)`
    }} data-id="element-83" data-mp="2gh9orwismlvq">
        <img src={before} alt={`${alt} — before`} draggable={false} className="h-full w-full object-cover" data-id="element-84" data-mp="2qctl2k19rml0" />
      </div>

      <span className="pointer-events-none absolute left-4 top-4 rounded-full bg-navy-900/80 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/85 backdrop-blur" data-id="element-85" data-mp="1vqe9l9pl11kk">
        Before
      </span>
      <span className="pointer-events-none absolute right-4 top-4 rounded-full bg-brass px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-navy-900" data-id="element-86" data-mp="25otz588yo2ry">
        After
      </span>

      <div className="pointer-events-none absolute inset-y-0 w-px bg-white/90 shadow-[0_0_20px_rgba(0,0,0,0.5)]" style={{
      left: `${position}%`
    }} data-id="element-87" data-mp="2eh4bav3f2ibu">
        <div className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy-900 shadow-xl" data-id="element-88" data-mp="2tc241p2h99tg">
          <MoveHorizontal className="h-5 w-5" aria-hidden="true" data-id="element-89" data-mp="z0neb89gvadp" />
        </div>
      </div>

      <input type="range" min={0} max={100} step={0.5} value={position} onChange={event => setPosition(Number(event.target.value))} aria-label={`${alt} before and after comparison`} className="absolute inset-x-0 bottom-0 h-12 w-full cursor-ew-resize opacity-0" data-id="element-90" data-mp="u2c3au7fqttw" />
    </div>;
}